import { Injectable, inject } from '@angular/core'
import { ShopfloorLayoutSpec } from './spec/shopfloor-layout-spec'
import { ShopfloorLayoutSpecEdge } from './shopfloor-layout-spec-edge'
import { ShopfloorLayoutContext } from './shopfloor-layout-context'
import { ShopfloorLayoutSpecNode } from './shopfloor-layout-spec-node'
import { CollectionUtil } from '@elderbyte/ngx-starter'
import {
  IShopFloorEditorDialogOptions,
  ShopfloorLayoutEditorDetailDialogComponent,
} from './shopfloor-layout-editor-detail-dialog/shopfloor-layout-editor-detail-dialog.component'
import { Observable } from 'rxjs'
import { filter, map } from 'rxjs/operators'
import { MatDialog } from '@angular/material/dialog'
import { GroupedEdge } from './grouped-edge'
import { GroupedEdgeTarget } from './grouped-edge-target'
import {
  IShopFloorEditorEdgeDialogOptions,
  ShopfloorLayoutEditorEdgeDialogComponent,
} from './shopfloor-layout-editor-edge-dialog/shopfloor-layout-editor-edge-dialog.component'

@Injectable({
  providedIn: 'root',
})
export class ShopfloorEditService {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private dialog = inject(MatDialog)

  /***************************************************************************
   *                                                                         *
   * Public  methods                                                         *
   *                                                                         *
   **************************************************************************/

  public openNodeEditDialog(
    context: ShopfloorLayoutContext,
    specId: string,
    specFacility: string,
    groupedEdges: GroupedEdge[],
    selectedEdge?: GroupedEdge
  ): Observable<ShopfloorLayoutSpec> {
    const dialogRef = this.dialog.open(ShopfloorLayoutEditorDetailDialogComponent, {
      minWidth: '70vw',
      height: '70vh',
      data: <IShopFloorEditorDialogOptions>{
        context,
        groupedEdges,
        selectedEdge,
      },
    })

    return dialogRef.afterClosed().pipe(
      filter((x) => !!x),
      map((updatedGroupedEdge: GroupedEdge) => {
        const updatedEdges = this.updateGroupedEdges(groupedEdges, updatedGroupedEdge)
        return this.buildSpec(specId, specFacility, updatedEdges)
      })
    )
  }

  public openEdgeEditDialog(
    context: ShopfloorLayoutContext,
    spec: ShopfloorLayoutSpec,
    edge: ShopfloorLayoutSpecEdge
  ): Observable<ShopfloorLayoutSpec> {
    const dialogRef = this.dialog.open(ShopfloorLayoutEditorEdgeDialogComponent, {
      minWidth: '70vw',
      height: '70vh',
      data: <IShopFloorEditorEdgeDialogOptions>{
        context: context,
        spec: spec,
        edge: edge,
      },
    })

    return dialogRef.afterClosed().pipe(
      filter((x) => !!x),
      map((changedSpec: ShopfloorLayoutSpec) => {
        return changedSpec
      })
    )
  }

  public buildGroupedEdges(
    spec: ShopfloorLayoutSpec,
    context: ShopfloorLayoutContext
  ): GroupedEdge[] {
    // Create indexes to retrieve nodes from their id
    const indexes: Map<string, ShopfloorLayoutSpecNode> = new Map()
    context.nodes.forEach((n) => indexes.set(n.id, n))

    // Edges grouped by source
    const groupedBySource: Map<string, ShopfloorLayoutSpecEdge[]> = CollectionUtil.groupByField(
      spec.edges,
      'source'
    )

    // Build groupedEdge objects
    const groupedEdges: GroupedEdge[] = []
    for (const [key, value] of groupedBySource) {
      const targets = value.map((e) => this.buildTarget(indexes, e))
      groupedEdges.push(new GroupedEdge(indexes.get(key), targets))
    }

    return groupedEdges.sort((e1, e2) =>
      e1?.source?.workCenter?.number?.localeCompare(e2?.source?.workCenter?.number)
    )
  }

  /***************************************************************************
   *                                                                         *
   * Private methods                                                         *
   *                                                                         *
   **************************************************************************/

  private buildSpec(
    specId: string,
    specFacility: string,
    groupedEdges: GroupedEdge[]
  ): ShopfloorLayoutSpec {
    const newEdges: ShopfloorLayoutSpecEdge[] = []
    groupedEdges.forEach((ge) =>
      ge.targets.forEach((t) => {
        newEdges.push(ShopfloorLayoutSpecEdge.build(ge.source, t.node, t.transportCost))
      })
    )

    return new ShopfloorLayoutSpec(specId, specFacility, newEdges)
  }

  private updateGroupedEdges(
    groupedEdges: GroupedEdge[],
    updatedGroupedEdge: GroupedEdge
  ): GroupedEdge[] {
    let groupedEdgesToUpdate = groupedEdges.map((ge) => new GroupedEdge(ge.source, ge.targets))
    if (updatedGroupedEdge) {
      const edgeToUpdate = groupedEdgesToUpdate.find(
        (ge) => ge.source.id === updatedGroupedEdge.source.id
      )
      if (edgeToUpdate) {
        if (updatedGroupedEdge.targets.length === 0) {
          // Remove grouped edge if no targets are selected
          groupedEdgesToUpdate = groupedEdgesToUpdate.filter(
            (ge) => ge.source.id !== updatedGroupedEdge.source.id
          )
        } else {
          // If at least one target is selected, update the grouped edge in the model
          edgeToUpdate.targets = updatedGroupedEdge.targets
        }
      } else {
        // If the edge is not in the model yet, add it
        groupedEdgesToUpdate.push(updatedGroupedEdge)
      }
    }
    return groupedEdgesToUpdate
  }

  private buildTarget(indexes: Map<string, ShopfloorLayoutSpecNode>, e: ShopfloorLayoutSpecEdge) {
    const node = indexes.get(e.target)
    return new GroupedEdgeTarget(node?.id, node, e.transportCost)
  }
}
